import { useCallback, useState } from "react"
import { Button } from "@/components/ui/button"
import { Check, Copy, ExternalLink, FileText } from "lucide-react"

interface BoletoPaymentProps {
  boletoUrl?: string
  boletoBarcode?: string
  boletoDueAt?: string
}

export function BoletoPayment({ boletoUrl, boletoBarcode, boletoDueAt }: BoletoPaymentProps) {
  const [copied, setCopied] = useState(false)

  const handleCopyBarcode = useCallback(() => {
    if (boletoBarcode) {
      navigator.clipboard.writeText(boletoBarcode)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }, [boletoBarcode])

  const formattedDueDate = boletoDueAt
    ? new Date(boletoDueAt).toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : null

  return (
    <div className="flex flex-col items-center gap-6 py-4">
      <div className="text-center">
        <h3 className="text-lg font-semibold">Pagamento via Boleto</h3>
        <p className="text-sm text-gray-500">
          O boleto foi gerado. Pague até a data de vencimento.
        </p>
      </div>

      <div className="flex h-24 w-24 items-center justify-center rounded-full bg-blue-50">
        <FileText className="h-12 w-12 text-blue-500" />
      </div>

      {formattedDueDate && (
        <div className="rounded-md bg-amber-50 px-4 py-2 text-sm text-amber-700">
          Vencimento: <strong>{formattedDueDate}</strong>
        </div>
      )}

      <div className="rounded-md bg-blue-50 px-4 py-3 text-center text-sm text-blue-700">
        A ativação da sua assinatura ocorrerá após a compensação do boleto (1-3 dias úteis).
      </div>

      {boletoBarcode && (
        <div className="w-full space-y-2">
          <label className="text-sm font-medium text-gray-600">Código de barras</label>
          <div className="rounded-md border bg-gray-50 p-3">
            <p className="text-xs break-all text-gray-600">{boletoBarcode}</p>
          </div>
          <Button variant="outline" className="w-full" onClick={handleCopyBarcode}>
            {copied ? (
              <>
                <Check className="mr-2 h-4 w-4 text-green-500" />
                Copiado!
              </>
            ) : (
              <>
                <Copy className="mr-2 h-4 w-4" />
                Copiar código de barras
              </>
            )}
          </Button>
        </div>
      )}

      {boletoUrl && (
        <Button variant="default" className="w-full" asChild>
          <a href={boletoUrl} target="_blank" rel="noopener noreferrer">
            <ExternalLink className="mr-2 h-4 w-4" />
            Abrir boleto em PDF
          </a>
        </Button>
      )}
    </div>
  )
}
